import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  billingPreviewQuery,
  listOverdueInvoicesQuery,
  listStudentInvoicesQuery,
  postStudentBillsQuery
} from "../../../Graphql/querries/education/billingQuery";
import {
  BillingPreviewInput,
  BillingPreviewResponse,
  ListOverdueInvoicesResponse,
  ListStudentInvoicesResponse,
  PostStudentBillsInput,
  PostStudentBillsResponse,
  StudentInvoice
} from "../../../components/Education/Billing/types/billing";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useBillingPreview = (input: BillingPreviewInput | null) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["billingPreview", input?.billingPeriodId, input?.dueDate],
    enabled: Boolean(input?.billingPeriodId),
    queryFn: async () => {
      const data = await fetchGraphQL<
        GraphQLData<BillingPreviewResponse, "billingPreview">,
        { input: BillingPreviewInput | null }
      >(billingPreviewQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "billingPreview");
      if (response.code !== API_RESPONSE_CODE.OK) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return null;
      }
      return response.preview ?? null;
    }
  });
};

export const usePostStudentBills = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: PostStudentBillsInput) => {
      const data = await fetchGraphQL<
        GraphQLData<PostStudentBillsResponse, "postStudentBills">,
        { input: PostStudentBillsInput }
      >(postStudentBillsQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "postStudentBills");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.result;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useStudentInvoices = (studentId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["studentInvoices", studentId],
    enabled: Boolean(studentId),
    queryFn: async (): Promise<StudentInvoice[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListStudentInvoicesResponse, "listStudentInvoices">,
        { input: { studentId?: string; size: number } }
      >(listStudentInvoicesQuery, { input: { studentId, size: 50 } }, setAlert);
      const response = unwrapGraphQLOperation(data, "listStudentInvoices");
      if (response.code !== API_RESPONSE_CODE.OK) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return [];
      }
      return response.invoices ?? [];
    }
  });
};

export const useOverdueInvoices = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["overdueInvoices"],
    queryFn: async (): Promise<StudentInvoice[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListOverdueInvoicesResponse, "listOverdueInvoices">,
        { input: { size: number } }
      >(listOverdueInvoicesQuery, { input: { size: 10 } }, setAlert);
      const response = unwrapGraphQLOperation(data, "listOverdueInvoices");
      if (response.code !== API_RESPONSE_CODE.OK) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return [];
      }
      return response.invoices ?? [];
    }
  });
};
